import { useValue } from "@legendapp/state/react";
import { FivetranServiceEnum, googleSheetsSchema } from "@repo/fivetran/types";
import { Alert, AlertDescription } from "@repo/ui/ui/alert";
import { toast } from "sonner";

import { datasources$ } from "~/stores/datasources";
import { DestinationField, SchemaField } from "./connection-fields";
import { ConnectionFormShell } from "./connection-form-shell";
import { DestinationSetup } from "./destination-setup";
import {
  type ConnectionFormProps,
  useConnectionForm,
} from "./use-connection-form";

/** Reactively shows the "share your sheet" message once a Fivetran destination is provisioned. */
const GoogleSheetsShareAlert = ({ datasourceId }: { datasourceId: string }) => {
  const email = useValue(
    datasources$[datasourceId]?.fivetranDestinations?.serviceAccountEmail,
  );

  if (!datasourceId || !email) return null;

  return (
    <Alert
      className="cursor-pointer transition-colors hover:bg-accent/50"
      onClick={() => {
        navigator.clipboard.writeText(email);
        toast.success("Email copied to clipboard");
      }}
    >
      <AlertDescription>
        Please share your Google Sheet with{" "}
        <span className="font-medium font-mono text-foreground">{email}</span>{" "}
        to allow access.
      </AlertDescription>
    </Alert>
  );
};

export const GoogleSheetsFormPage = ({
  onConnected,
  onCancel,
}: ConnectionFormProps = {}) => {
  const { form, embedded, formId } = useConnectionForm({
    service: FivetranServiceEnum.enum.google_sheets,
    schema: googleSheetsSchema,
    defaultConfig: {
      schema: FivetranServiceEnum.enum.google_sheets,
      table: "",
      named_range: "",
      sheet_id: "",
    },
    onConnected,
  });

  return (
    <ConnectionFormShell
      form={form}
      formId={formId}
      icon="sheets"
      submitLabel="Authenticate"
      embedded={embedded}
      onCancel={onCancel}
    >
      <DestinationField form={form} />

      <div className="grid grid-cols-2 gap-4">
        <SchemaField form={form} />
        <form.AppField name="config.table">
          {(field) => <field.TextField label="Table" placeholder="my_table" />}
        </form.AppField>
      </div>

      <form.Subscribe selector={(state) => state.values.datasourceId}>
        {(datasourceId) => (
          <>
            <DestinationSetup datasourceId={datasourceId} />
            <GoogleSheetsShareAlert datasourceId={datasourceId} />
          </>
        )}
      </form.Subscribe>

      <form.AppField name="config.sheet_id">
        {(field) => (
          <field.TextField label="Sheet ID" placeholder="1234567890" />
        )}
      </form.AppField>

      <form.AppField name="config.named_range">
        {(field) => (
          <field.TextField label="Named Range" placeholder="my_named_range" />
        )}
      </form.AppField>
    </ConnectionFormShell>
  );
};
